"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.createProjectTimeline = exports.mountSvg = exports.generateTimeline = exports.countAnimatedElements = exports.DEFAULT_IN_OUT = exports.DEFAULT_DELAY = void 0;
const gsap_1 = require("gsap");
const ui_common_animation_effects_1 = require("@desygner/ui-common-animation-effects");
const utils_1 = require("./utils");
exports.DEFAULT_DELAY = 0.3;
exports.DEFAULT_IN_OUT = 1; // seconds
function countAnimatedElements(animate, elements) {
    var _a, _b;
    const hasElementAnimations = animate.elementAnimations && Object.keys(animate.elementAnimations).length;
    const hasPageAnimation = animate.animation !== 'no_animation' &&
        (0, utils_1.isFunction)(ui_common_animation_effects_1.AnimationMap[animate.animation]);
    if (!hasElementAnimations && !hasPageAnimation) {
        return 0;
    }
    let animatedElementCount = 0;
    for (let i = 0; i < elements.length; i++) {
        const element = elements[i];
        const id = element.id || element.getAttribute('data-id') || 0;
        const animationType = hasElementAnimations
            ? (_b = (_a = animate.elementAnimations) === null || _a === void 0 ? void 0 : _a[id]) !== null && _b !== void 0 ? _b : animate.animation
            : animate.animation;
        const animation = ui_common_animation_effects_1.AnimationMap[animationType];
        if (!animation)
            continue;
        animatedElementCount += 1;
    }
    return animatedElementCount;
}
exports.countAnimatedElements = countAnimatedElements;
function calculateAnimationDelay(elementIndex, baseDelay, isAnimatedTogether, onlyAnimateSelectedElement) {
    if (isAnimatedTogether || onlyAnimateSelectedElement)
        return 0;
    if (elementIndex === 0)
        return baseDelay;
    return undefined;
}
function calculateBaseDelay(animatedElementCount, inOutDurationInSec) {
    return animatedElementCount === 0
        ? exports.DEFAULT_DELAY
        : (0, utils_1.round)((inOutDurationInSec / (animatedElementCount / 2)) * exports.DEFAULT_DELAY);
}
function getTargetDuration(animate, duration, baseDelay, animatedElementCount) {
    return animate.animateElementsTogether
        ? duration
        : (0, utils_1.round)((duration - baseDelay * (animatedElementCount - 1)) /
            animatedElementCount);
}
function getDuration(inOutDurationInSec, yoyo, semiPauseTime) {
    return yoyo ? semiPauseTime + inOutDurationInSec : inOutDurationInSec;
}
function getSemiPauseTime(baseDelay) {
    return baseDelay / 2;
}
function generateTimeline(animate, svg, options) {
    const { yoyo, onlyAnimateSelectedElement } = options !== null && options !== void 0 ? options : {};
    const svgElement = (0, utils_1.prepareGroupAnimations)(svg, animate);
    const elements = getSortedElements(svgElement);
    const hasElementAnimations = animate.elementAnimations &&
        Object.keys(animate.elementAnimations).length > 0;
    const hasPageAnimation = animate.animation !== 'no_animation' &&
        (0, utils_1.isFunction)(ui_common_animation_effects_1.AnimationMap[animate.animation]);
    const animatedElementCount = countAnimatedElements(animate, elements);
    const inOutDurationInSec = (0, utils_1.convertToSeconds)(animate.inOutDuration, exports.DEFAULT_IN_OUT);
    const baseDelay = calculateBaseDelay(animatedElementCount, inOutDurationInSec);
    const targetDuration = getTargetDuration(animate, inOutDurationInSec, baseDelay, animatedElementCount);
    const semiPauseTime = getSemiPauseTime(baseDelay);
    const duration = getDuration(inOutDurationInSec, !!yoyo, semiPauseTime);
    console.log(`Duration ${duration}, BaseDelay ${baseDelay}, Target Duration ${targetDuration}, Count ${animatedElementCount}`);
    const parentTimeline = gsap_1.gsap.timeline({
        duration: targetDuration,
        paused: true,
        onComplete: function () {
            if (yoyo) {
                this['reverse']();
            }
        },
    });
    if (!hasElementAnimations && !hasPageAnimation) {
        console.log(`No animation found ${animate.animation}`);
        return;
    }
    elements.forEach((element, index) => {
        var _a, _b, _c;
        if (onlyAnimateSelectedElement &&
            !element.classList.contains('selected') &&
            element.querySelectorAll('.selected').length === 0) {
            return;
        }
        const id = element.id || element.getAttribute('data-id') || 0;
        const animationType = hasElementAnimations
            ? (_b = (_a = animate.elementAnimations) === null || _a === void 0 ? void 0 : _a[id]) !== null && _b !== void 0 ? _b : animate.animation
            : animate.animation;
        const elementWrapper = ui_common_animation_effects_1.utils.createAnimationContainer(element);
        const animation = (_c = ui_common_animation_effects_1.AnimationMap[animationType]) === null || _c === void 0 ? void 0 : _c.call(ui_common_animation_effects_1.AnimationMap, elementWrapper);
        if (!animation) {
            return;
        }
        const animationDelay = calculateAnimationDelay(index, baseDelay, !!animate.animateElementsTogether, !!onlyAnimateSelectedElement);
        parentTimeline.add(animation, animationDelay);
    });
    if (yoyo) {
        parentTimeline.to({}, { duration: semiPauseTime }, `>}`);
    }
    // This wrapper ensures the timeline is on run for the exact duration specified.
    return gsap_1.gsap.to(parentTimeline, {
        duration: duration,
        progress: 1,
    });
}
exports.generateTimeline = generateTimeline;
function mountSvg(elementTarget, page) {
    var _a, _b, _c;
    const svgElement = document.createElement('div');
    svgElement.style.position = 'absolute';
    svgElement.innerHTML = page.content;
    svgElement.setAttribute('id', `svg-${page.id}-container`);
    svgElement.style.height = '100%';
    svgElement.style.marginLeft = 'auto';
    svgElement.style.marginRight = 'auto';
    svgElement.style.left = '0';
    svgElement.style.right = '0';
    elementTarget.appendChild(svgElement);
    const svg = ((_a = elementTarget.querySelector(`#svg-${page.id}-container`)) === null || _a === void 0 ? void 0 : _a.firstChild);
    const svgHeight = parseFloat(svg.getAttribute('height'));
    const svgWidth = parseFloat(svg.getAttribute('width'));
    svg.setAttribute('id', `svg-${page.id}`);
    svg.setAttribute('viewBox', `0 0 ${svgWidth} ${svgHeight}`);
    svg.setAttribute('width', '100%');
    svg.setAttribute('height', '100%');
    svg.style.display = 'none';
    const clipPath = document.createElementNS('http://www.w3.org/2000/svg', 'clipPath');
    clipPath.setAttribute('id', `clip-${page.id}`);
    const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    rect.setAttribute('x', '0');
    rect.setAttribute('y', '0');
    rect.setAttribute('width', String(svgWidth));
    rect.setAttribute('height', String(svgHeight));
    clipPath.appendChild(rect);
    svg.appendChild(clipPath);
    (_b = svg
        .querySelector('.main')) === null || _b === void 0 ? void 0 : _b.setAttribute('clip-path', `url(#clip-${page.id})`);
    (_c = svg
        .querySelector('.background')) === null || _c === void 0 ? void 0 : _c.setAttribute('clip-path', `url(#clip-${page.id})`);
    return svg;
}
exports.mountSvg = mountSvg;
function createProjectTimeline(elementTarget, metadata, pages, options) {
    const timeline = gsap_1.gsap.timeline({ paused: !!(options === null || options === void 0 ? void 0 : options.paused) });
    pages.forEach(function (page) {
        const { id } = page;
        const metadataPage = metadata.pages[id];
        if (!metadataPage) {
            return;
        }
        const svg = mountSvg(elementTarget, page);
        const pageTimeline = generateTimeline(metadataPage, svg, {
            yoyo: !!(options === null || options === void 0 ? void 0 : options.yoyo),
            onlyAnimateSelectedElement: false,
        });
        if (pageTimeline) {
            timeline.add(() => {
                svg.style.display = 'block';
            }, `>`);
            timeline.add(pageTimeline, '>');
            if (options === null || options === void 0 ? void 0 : options.yoyo) {
                timeline.add(() => {
                    svg.style.display = 'none';
                }, `>${pageTimeline.duration()}`);
            }
            else {
                timeline.add(() => {
                    svg.style.display = 'none';
                }, `>`);
            }
        }
    });
    return timeline;
}
exports.createProjectTimeline = createProjectTimeline;
function getSortedElements(svg, selector = '#svg_background_image, .main > .element') {
    const pageElements = [...svg.querySelectorAll(selector)];
    const targetBoxes = pageElements.reduce((acc, element) => {
        const box = (0, utils_1.getBBox)(element);
        const id = element.id || element.getAttribute('data-id') || 0;
        if (box) {
            acc[id] = { x: box.x, y: box.y };
        }
        return acc;
    }, {});
    pageElements.sort((a, b) => {
        const aId = a.id || a.getAttribute('data-id') || 0;
        const bId = b.id || b.getAttribute('data-id') || 0;
        const aBoundingBox = targetBoxes[aId];
        const bBoundingBox = targetBoxes[bId];
        return sortByDistanceFromTopLeftCorner(aBoundingBox, bBoundingBox);
    });
    return pageElements;
}
function sortByDistanceFromTopLeftCorner({ x: ax, y: ay }, { x: bx, y: by }) {
    return ay - by || ax - bx;
}
//# sourceMappingURL=animation-timeline.js.map